/**
 * The optimistic-mutation wrapper — docs/ARCHITECTURE.md §5.5.
 *
 * Apply → toast → commit. On failure restore the pre-image, toast the error and
 * (for network-class errors only) retry once after 1200 ms. Operations sharing a
 * `key` are serialized, so a fast double-tap on ▲/▼ can never land out of order.
 *
 * DEVIATION (P2-05): §5.5 typed `optimistic` as `(draft: Store) => void`, which
 * implies immer. Immer is not on the dependency allowlist (docs/PLAN.md §1), so
 * `optimistic` returns a *patch* instead. Same call sites, one fewer package.
 */

import type { QdStore } from './types.ts';

export interface MutateApi {
  getState: () => QdStore;
  setState: (patch: Partial<QdStore>) => void;
}

/**
 * Adapter from a slice's `(set, get)` pair to `MutateApi`, so slice files can
 * call `mutate()` without importing zustand.
 */
export function storeApi(
  get: () => QdStore,
  set: (patch: Partial<QdStore>) => void,
): MutateApi {
  return { getState: get, setState: set };
}

export interface MutateOpts<T> {
  /** e.g. `vote:<entryId>:<uid>` — dedupes and orders */
  key: string;
  optimistic: (s: QdStore) => Partial<QdStore>;
  commit: () => Promise<T>;
  /** fired immediately, like the prototype */
  toast?: string;
  toastErr?: boolean;
  rollbackToast?: string;
}

export const DEFAULT_ROLLBACK_TOAST = "✕ COULDN'T SAVE — TRY AGAIN";
export const RETRY_DELAY_MS = 1200;

export interface InFlightWrite {
  op: string;
  /** Last mark, for the TTL sweep. */
  ts: number;
  /** Writes for this key whose commit has not settled yet. */
  open: number;
  /**
   * The store as each recent optimistic write left it, newest last — the value
   * §2.3 compares an incoming echo against.
   *
   * A LIST, and it outlives the commit, because neither simplification holds:
   *
   *   - The LIVE store is not evidence. A snapshot refetch that was already
   *     open when the write started lands with pre-write data and replaces
   *     `list` wholesale, so by the time our own echo arrives the store no
   *     longer holds what we asked for. Comparing against it accuses a
   *     stranger of our own write.
   *   - ONE value is not enough. `mutate` serializes two taps on the same row
   *     but the network does not: tap B then A, and A is already in flight
   *     when B's echo comes back. B's echo has to still be recognisable as
   *     ours, or the store flickers back to B under a toast.
   */
  echoes: Snapshot[];
}

/**
 * Rows we are currently writing, for §2.3 echo suppression. Entries expire
 * after 8 s so a dropped realtime event cannot wedge the map.
 */
export const inFlight = new Map<string, InFlightWrite>();
const IN_FLIGHT_TTL = 8000;
/** A burst of taps on one row inside the TTL. Four is already generous. */
const IN_FLIGHT_ECHOES = 4;

function markInFlight(key: string, after: Snapshot): void {
  const now = Date.now();
  for (const [k, v] of inFlight) if (now - v.ts > IN_FLIGHT_TTL) inFlight.delete(k);
  const cur = lookupInFlight(key);
  inFlight.set(key, {
    op: key.split(':')[0] ?? key,
    ts: now,
    open: (cur?.open ?? 0) + 1,
    echoes: [...(cur?.echoes ?? []), after].slice(-IN_FLIGHT_ECHOES),
  });
}

/**
 * A settled write stops being able to toast, but its echo record STAYS until
 * the TTL sweeps it: the echo it is waiting for has not necessarily arrived.
 * Dropping the record here is what made a second tap disown the first one's
 * echo, since `mutate` only starts the second write once the first has settled.
 */
function releaseInFlight(key: string): void {
  // `lookupInFlight`, not a bare `get`: a commit slow enough to outlive the TTL
  // must not resurrect the record it already expired out of.
  const cur = lookupInFlight(key);
  if (cur) inFlight.set(key, { ...cur, open: Math.max(0, cur.open - 1) });
}

/** The entry for `key`, or undefined once it is past the TTL. */
export function lookupInFlight(key: string): InFlightWrite | undefined {
  const cur = inFlight.get(key);
  if (!cur) return undefined;
  if (Date.now() - cur.ts > IN_FLIGHT_TTL) {
    inFlight.delete(key);
    return undefined;
  }
  return cur;
}

/**
 * Drop every record. Only `teardown` calls it — records now outlive their
 * commits, so without this a store torn down mid-write leaves its keys behind
 * for the next store in the process, which then reads another session's writes
 * as its own. In production there is exactly one store; in a test file there is
 * one per `beforeEach`, which is where this bites.
 *
 * The map is a module singleton, so this is the one place where two coexisting
 * stores are not isolated from each other. That is the same shared-map bargain
 * `inFlight` has always been, and the cost of getting it wrong is a toast that
 * fails to fire, not corrupted data.
 */
export function clearInFlight(): void {
  inFlight.clear();
}

/** The data fields a rollback has to restore. UI state is never rolled back. */
const SNAPSHOT_KEYS = [
  'users',
  'list',
  'pts',
  'videos',
  'potentials',
  'tiers',
  'tierOrder',
  'skips',
  'intOrder',
  // `intSkips` beside `skips`, its GOTY twin: without it a failed SKIP FOR NOW
  // toasts the error and leaves the game rotated anyway — and the NEXT skip
  // builds on the un-rolled-back array and persists both.
  'intSkips',
  'steamLink',
  // MY GAMES (AA phase 2): the shelf and the scores are written optimistically
  // like every other board, so a failed write has to put them back.
  'backlog',
  'sends',
  'watch',
  'board',
  'scores',
] as const;

type SnapshotKey = (typeof SNAPSHOT_KEYS)[number];
export type Snapshot = Pick<QdStore, SnapshotKey>;

export function snapshot(s: QdStore): Snapshot {
  const out = {} as Record<SnapshotKey, unknown>;
  for (const k of SNAPSHOT_KEYS) out[k] = s[k];
  return out as Snapshot;
}

const NETWORK_CODES = new Set([
  'OFFLINE',
  'UPSTREAM_STATUS',
  'UPSTREAM_TIMEOUT',
  'RATE_LIMITED',
  'INTERNAL',
]);

function isNetworkClass(e: unknown): boolean {
  const code = (e as { code?: unknown } | null)?.code;
  return typeof code === 'string' ? NETWORK_CODES.has(code) : true;
}

const queues = new Map<string, Promise<unknown>>();

export async function mutate<T>(api: MutateApi, opts: MutateOpts<T>): Promise<T | undefined> {
  const prev = queues.get(opts.key) ?? Promise.resolve();
  const run = prev.then(
    () => runOne(api, opts),
    () => runOne(api, opts),
  );
  queues.set(
    opts.key,
    run.catch(() => undefined),
  );
  try {
    return await run;
  } finally {
    // Let the map drain once this key is idle again.
    if (queues.get(opts.key) === run) queues.delete(opts.key);
  }
}

/**
 * Resolves once no mutation is in flight (D10).
 *
 * `switchCrew` has to drain BEFORE it repoints the server-side active crew,
 * and this is why: the board RPCs stamp `crew_id := my_crew_id()` in their own
 * bodies, so a commit still in flight — or, worse, the 1200 ms retry of one
 * that just failed — would land the OLD crew's action in the NEW crew. The
 * write looks successful and ends up somewhere nobody will look for it.
 *
 * A SINGLE `allSettled` PASS IS NOT ENOUGH, which is the whole subtlety.
 * `mutate` chains same-key writes: it stores `prev.then(...)`, so a write
 * arriving mid-drain REPLACES the promise we are awaiting with a later one.
 * Awaiting the values we sampled would then return while that newer write is
 * still open. So this re-samples until the map is empty.
 *
 * The pass bound is a backstop, not a design: only an unbroken stream of new
 * writes can reach it, which in practice means a stuck retry loop rather than
 * a user. Returning early there is the honest failure — the caller gets on
 * with the switch and at worst one write lands in the wrong crew, which is
 * strictly better than hanging the UI forever.
 */
const MAX_DRAIN_PASSES = 20;

export async function whenIdle(): Promise<void> {
  for (let pass = 0; pass < MAX_DRAIN_PASSES; pass++) {
    if (queues.size === 0) return;
    await Promise.allSettled([...queues.values()]);
  }
}

async function runOne<T>(api: MutateApi, opts: MutateOpts<T>): Promise<T | undefined> {
  const pre = snapshot(api.getState());
  api.setState(opts.optimistic(api.getState()));
  // Taken AFTER the optimistic patch: this is the shape we are asking the
  // server for, and §2.3 compares the echo against it rather than against a
  // live store that anything may have moved in the meantime.
  markInFlight(opts.key, snapshot(api.getState()));
  if (opts.toast) api.getState().toast(opts.toast, opts.toastErr);

  try {
    return await opts.commit();
  } catch (e) {
    if (isNetworkClass(e)) {
      await delay(RETRY_DELAY_MS);
      try {
        return await opts.commit();
      } catch {
        /* fall through to rollback */
      }
    }
    api.setState(pre);
    api.getState().toast(opts.rollbackToast ?? DEFAULT_ROLLBACK_TOAST, true);
    return undefined;
  } finally {
    releaseInFlight(opts.key);
  }
}

function delay(ms: number): Promise<void> {
  return new Promise((res) => setTimeout(res, ms));
}
